import { useMutation } from "@tanstack/react-query";
import type { CustomerView } from "@portal/schemas";
import { Badge, Button, Card, CardContent, CardHeader } from "@portal/ui";
import { useMemo, useState } from "react";
import { changeCustomerStatus } from "../../api/client";
import { useCustomerPermissions, useCustomers, useInvalidate } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { DataTable, createDataTableColumns } from "../../components/DataTable";
import { InlineError } from "../../components/InlineError";
import { CustomerFormDialog } from "./CustomerFormDialog";
import { CardTitleWithHint } from "../../components/Hint";
import { Archive, ArchiveRestore, Eye, EyeOff, Pencil, Plus } from "lucide-react";

const col = createDataTableColumns<CustomerView>();
const CUSTOMER_FILTERS = [{ columnId: "status", label: "状態" }] as const;

export function CustomersPage() {
  const [showArchived, setShowArchived] = useState(false);
  const customers = useCustomers(showArchived ? undefined : "ACTIVE");
  const permissions = useCustomerPermissions();
  const canManage = permissions.data?.canManage ?? false;
  const invalidate = useInvalidate();
  const status = useMutation({
    mutationFn: (c: CustomerView) =>
      changeCustomerStatus(c.id, {
        revision: c.revision,
        status: c.status === "ACTIVE" ? "ARCHIVED" : "ACTIVE",
      }),
    onSuccess: () => invalidate.customers(),
  });
  const columns = useMemo(
    () =>
      col.columns([
        col.accessor("name", { header: "顧客名", meta: { className: "font-medium" } }),
        col.accessor((c) => c.aliases.join("、"), {
          id: "aliases",
          header: "別名",
          meta: { className: "text-muted-foreground" },
        }),
        col.accessor((c) => (c.status === "ACTIVE" ? "有効" : "アーカイブ"), {
          id: "status",
          header: "状態",
          cell: (info) => (
            <Badge variant={info.row.original.status === "ACTIVE" ? "success" : "outline"}>
              {info.getValue()}
            </Badge>
          ),
        }),
        ...(canManage
          ? [
              col.display({
                id: "actions",
                header: "",
                meta: { className: "w-24" },
                cell: (info) => {
                  const c = info.row.original;
                  return (
                    <div className="flex gap-2">
                      <CustomerFormDialog
                        customer={c}
                        trigger={
                          <Button variant="ghost" size="icon-sm" aria-label="編集" title="編集">
                            <Pencil />
                          </Button>
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => status.mutate(c)}
                        disabled={status.isPending}
                        aria-label={c.status === "ACTIVE" ? "アーカイブ" : "復元"}
                        title={c.status === "ACTIVE" ? "アーカイブ" : "復元"}
                      >
                        {c.status === "ACTIVE" ? <Archive /> : <ArchiveRestore />}
                      </Button>
                    </div>
                  );
                },
              }),
            ]
          : []),
      ]),
    [canManage, status],
  );

  return (
    <Card data-testid="customers-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitleWithHint
            hint={
              canManage
                ? "いずれかのプロジェクトの管理者は、顧客を登録・編集できます。"
                : "閲覧のみです(編集はプロジェクトの管理者)。"
            }
          >
            顧客
          </CardTitleWithHint>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? <EyeOff /> : <Eye />}
            {showArchived ? "アーカイブを隠す" : "アーカイブも表示"}
          </Button>
          {canManage && (
            <CustomerFormDialog
              trigger={
                <Button>
                  <Plus />
                  顧客を登録
                </Button>
              }
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <CardStatus
          isPending={customers.isPending}
          isError={customers.isError}
          refetch={() => customers.refetch()}
        />
        <InlineError error={status.error} />
        {customers.data && (
          <DataTable
            columns={columns}
            data={customers.data.customers}
            getRowId={(c) => c.id}
            emptyText="顧客は登録されていません。"
            searchPlaceholder="顧客名・別名で検索"
            filters={showArchived ? CUSTOMER_FILTERS : undefined}
          />
        )}
      </CardContent>
    </Card>
  );
}
